import { AccountDetails, AllStats, AssignedUser } from '@/containers/Accounts/types';
import { AppUser } from '@/containers/Auth/types';
import { Identity } from '@/containers/Identities/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SkinTones } from 'emoji-picker-react';
import { User as AuthUser } from 'firebase/auth';
import { tokenStore } from '../storage/TokenStore';

// New types for multi-account support
interface SubAccount {
  account_id: string;
  account_name: string;
}

interface Account {
  account_id: string;
  account_name: string;
  account_type: 'root' | 'sub';
  parent_account_id?: string; // Direct parent account ID
  parent_account_key?: string; // Full hierarchy path (e.g., "11480092.11480025")
}
const appAccessToken = tokenStore.getAppAccessToken();
const firebaseAccessToken = tokenStore.getFirebaseAccessToken();

interface ExtraAppState {
  skinTone?: SkinTones;
}
type AppSliceState = {
  isTokenVerified: boolean;
  // User is maintained on Firestore Session
  // we are also storing on Redux for retaining as part of App State
  user: AuthUser | null;
  isUserLoading: boolean;
  appUser: AppUser | null;
  isTAndCAccepted?: boolean;
  rootAccounts: string[] | null;

  // Account Management (NEW)
  currentAccountId: string | null; // Currently selected account ID
  allAccounts: Account[] | null; // Flattened list of all accessible accounts
  subAccountsByRoot: Record<string, SubAccount[]>; // Sub-accounts grouped by root

  // Current Account Data (unchanged structure, different source)
  accountDetails: AccountDetails | null; // Details for currently selected account
  accountIdentities: Identity[]; // Identities for currently selected account
  assignedUsers: AssignedUser[] | null; // Users assigned to currently selected account
  stats: AllStats | null; // Global stats (unchanged)
  isStatsLoading?: boolean;
  appAccessToken?: string;
  firebaseAccessToken?: string;
  loginId?: number | null;
  extras?: ExtraAppState;
};

const initialState: AppSliceState = {
  isTokenVerified: false,
  isUserLoading: true,
  user: null,
  appUser: null,
  rootAccounts: null,

  // Account Management (NEW)
  currentAccountId: null,
  allAccounts: null,
  subAccountsByRoot: {},

  // Current Account Data
  accountDetails: null,
  accountIdentities: [],
  assignedUsers: null,
  stats: null,
  isStatsLoading: false,
  firebaseAccessToken: firebaseAccessToken ?? undefined,
  appAccessToken: appAccessToken ?? undefined,
  extras: {
    skinTone: SkinTones.NEUTRAL,
  },
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    reset: () => {
      return {
        ...initialState,
        isTokenVerified: true,
        isUserLoading: false,
      };
    },
    setIsTokenVerified: (state: AppSliceState, action: PayloadAction<boolean>) => {
      state.isTokenVerified = action.payload;
    },
    setUser: (state: AppSliceState, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload;
    },
    setIsUserloading: (state: AppSliceState, action: PayloadAction<boolean>) => {
      state.isUserLoading = action.payload;
    },
    setLoginId: (state: AppSliceState, action: PayloadAction<number>) => {
      state.loginId = action.payload;
    },
    setAppUser: (state: AppSliceState, action: PayloadAction<AppUser | null>) => {
      const appUser = state.appUser;
      state.appUser = appUser ? { ...appUser, ...action.payload } : action.payload;
    },
    setRootAccounts: (state: AppSliceState, action: PayloadAction<string[] | null>) => {
      state.rootAccounts = action.payload;
    },
    setAccountDetails: (state: AppSliceState, action: PayloadAction<AccountDetails>) => {
      state.accountDetails = action.payload;
    },
    setStats: (state: AppSliceState, action: PayloadAction<AllStats>) => {
      state.stats = action.payload;
    },
    setIsStatsLoading: (state: AppSliceState, action: PayloadAction<boolean>) => {
      state.isStatsLoading = action.payload;
    },
    setAccountIdentities: (state: AppSliceState, action: PayloadAction<Identity[]>) => {
      state.accountIdentities = action.payload;
    },
    setAssignedUsers: (
      state: AppSliceState,
      action: PayloadAction<AssignedUser[] | null>
    ) => {
      state.assignedUsers = action.payload;
    },
    setIsTAndCAccepted: (state: AppSliceState, action: PayloadAction<boolean>) => {
      state.isTAndCAccepted = action.payload;
    },
    setAppAccessToken: (state: AppSliceState, action: PayloadAction<string>) => {
      state.appAccessToken = action.payload;
    },
    setFirebaseAccessToken: (state: AppSliceState, action: PayloadAction<string>) => {
      state.firebaseAccessToken = action.payload;
    },
    setExtraStateItems: (
      state: AppSliceState,
      action: PayloadAction<Partial<ExtraAppState>>
    ) => {
      state.extras = {
        ...state.extras,
        ...action.payload,
      };
    },
    // New action creators for multi-account support
    setCurrentAccountId: (state: AppSliceState, action: PayloadAction<string | null>) => {
      state.currentAccountId = action.payload;
    },
    setAllAccounts: (state: AppSliceState, action: PayloadAction<Account[] | null>) => {
      state.allAccounts = action.payload;
    },
    setSubAccountsByRoot: (
      state: AppSliceState,
      action: PayloadAction<Record<string, SubAccount[]>>
    ) => {
      // Merge new sub-accounts data with existing data
      state.subAccountsByRoot = {
        ...state.subAccountsByRoot,
        ...action.payload,
      };
    },
  },
});

export const {
  setIsTokenVerified,
  setUser,
  setIsUserloading,
  setAppAccessToken,
  setAccountDetails,
  setFirebaseAccessToken,
  setAppUser,
  setRootAccounts,
  setLoginId,
  setIsTAndCAccepted,
  setAccountIdentities,
  setAssignedUsers,
  setStats,
  setIsStatsLoading,
  setExtraStateItems,
  reset,
  // New action creators for multi-account support
  setCurrentAccountId,
  setAllAccounts,
  setSubAccountsByRoot,
} = appSlice.actions;

export default appSlice;

// Export types for use in other components
export type { Account, SubAccount };
